import React from 'react';
import { t } from '../../../../i18n';
import { getCurrentLocation, calculateDistance } from '../../../../utils/locationUtils';
import { showDialogWithDebounce } from './dialogUtils';

// ==================== 常量定义 ====================

/** 位置校验的最大允许距离（米） */
const MAX_VALIDATION_DISTANCE = 500;

// ==================== 类型定义 ====================

/**
 * 位置校验结果
 */
export interface LocationValidationResult {
    /**
     * 是否通过校验
     */
    isValid: boolean;
    /**
     * 司机位置信息（如果获取到）
     */
    driverLocation: { latitude: number; longitude: number } | null;
}

/**
 * 位置校验选项
 */
export interface LocationValidationOptions {
    /**
     * 派送地址纬度
     */
    addressLat?: number | null;
    /**
     * 派送地址经度
     */
    addressLon?: number | null;
    /**
     * 是否跳过500米距离限制
     */
    skipDistanceLimit?: boolean;
    /**
     * 校验失败时的回调（用于进入页面前的校验）
     */
    onValidationFailed?: () => void;
    /**
     * 校验通过时的回调（用于进入页面前的校验）
     */
    onValidationPassed?: () => void;
    /**
     * 是否在表单提交时校验（保留参数以兼容现有代码，但行为与进入页面前一致：200米外都禁止操作）
     * @deprecated 此参数已不再影响行为，200米外统一禁止操作
     */
    isFormSubmit?: boolean;
}

// ==================== 辅助函数 ====================

/**
 * 检查地址坐标是否有效
 */
function isValidAddress(addressLat: number | null | undefined, addressLon: number | null | undefined): boolean {
    return addressLat !== undefined && addressLat !== null && 
           addressLon !== undefined && addressLon !== null;
}

/**
 * 触发校验通过回调
 */
function triggerValidationPassed(callback?: () => void): void {
    if (callback) {
        callback();
    }
}

/**
 * 触发校验失败回调
 */
function triggerValidationFailed(callback?: () => void): void {
    if (callback) {
        callback();
    }
}

/**
 * 显示位置校验失败对话框（带防抖保护）
 */
function showLocationValidationFailedDialog(): void {
    showDialogWithDebounce('location_validation_failed', {
        content: React.createElement(
            'div',
            null,
            React.createElement(
                'div',
                { className: 'text-base font-semibold mb-3 text-center' },
                t('pages.delivery.orderDetail.locationWarningTitle')
            ),
            React.createElement(
                'div',
                { className: 'text-base text-center' },
                t('pages.delivery.orderDetail.locationWarningMessage')
            )
        ),
        actions: [
            {
                key: 'ok',
                text: t('pages.delivery.orderDetail.locationWarningButton')
            }
        ]
    });
}

/**
 * 创建校验通过的结果
 */
function createValidationPassedResult(driverLocation: { latitude: number; longitude: number } | null): LocationValidationResult {
    return {
        isValid: true,
        driverLocation
    };
}

/**
 * 创建校验失败的结果
 */
function createValidationFailedResult(driverLocation: { latitude: number; longitude: number } | null): LocationValidationResult {
    return {
        isValid: false,
        driverLocation
    };
}

// ==================== 主函数 ====================

/**
 * 校验司机位置是否在订单地址200m范围内
 * 
 * 规则：
 * 1. 如果派送地址没有经纬度，直接通过（地址本身有问题，不作考虑）
 * 2. 无定位权限：直接跳过校验，允许操作（driverLocation为null，后端会使用orderAddress）
 * 3. 有定位权限：快速获取位置并校验200米距离
 *    - 如果在200米内，通过
 *    - 如果在200米外，禁止操作，弹框提示
 * 
 * 性能优化：
 * - 优先使用缓存位置，减少定位时间（最快路径）
 * - 直接尝试获取位置，根据错误码判断（避免权限检查延迟）
 * - 使用网络定位（2秒超时），平衡速度和成功率
 * 
 * @param options 校验选项
 * @returns 校验结果（无权限时driverLocation为null，后端会使用orderAddress）
 */
export async function validateAccessCodeLocation(
    options: LocationValidationOptions
): Promise<LocationValidationResult> {
    const {
        addressLat,
        addressLon,
        skipDistanceLimit = false,
        onValidationFailed,
        onValidationPassed
    } = options;

    // 1. 如果派送地址没有经纬度，直接通过（地址本身有问题，不作考虑）
    if (!isValidAddress(addressLat, addressLon)) {
        triggerValidationPassed(onValidationPassed);
        return createValidationPassedResult(null);
    }

    // 2. 快速检查权限（优先使用缓存位置，避免权限检查延迟）
    // 先检查缓存，如果有缓存位置，直接使用（最快路径）
    const locationResult = await getCurrentLocation({
        useCache: true,
        requestPermissionIfDenied: false
    });

    // 3. 如果获取不到位置，根据失败原因决定是否跳过校验
    if (!locationResult.location) {
        // 3.1 如果是权限被拒绝，直接跳过校验，允许操作（无权限时跳过）
        if (locationResult.failureReason === 'permission_denied') {
            triggerValidationPassed(onValidationPassed);
            return createValidationPassedResult(null);
        }
        
        // 3.2 如果是定位失败（有权限但获取不到位置），也跳过校验，允许操作
        // 注意：driverLocation为null，后端会降级使用orderAddress的经纬度
        // 这种情况可能是：GPS信号弱、网络问题、超时等，不应该阻止用户操作
        triggerValidationPassed(onValidationPassed);
        return createValidationPassedResult(null);
    }

    // 4. 有位置信息，计算距离并校验
    const distance = calculateDistance(
        locationResult.location.latitude,
        locationResult.location.longitude,
        addressLat!,
        addressLon!
    );

    // 5. 如果在允许范围内，通过
    console.log("skipDistanceLimit",skipDistanceLimit)
    if ( skipDistanceLimit || distance <= MAX_VALIDATION_DISTANCE) {
        triggerValidationPassed(onValidationPassed);
        return createValidationPassedResult(locationResult.location);
    }

    // 6. 如果超出允许范围，禁止操作并显示提示（使用防抖保护）
    // 先触发失败回调（关闭loading），再显示弹窗
    triggerValidationFailed(onValidationFailed);
    showLocationValidationFailedDialog();
    return createValidationFailedResult(locationResult.location);
}
